import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

/** Parse a YouTube URL and return the embed URL with autoplay. */
export const toYouTubeEmbed = (url) => {
  if (!url) return "";
  try {
    const u = new URL(url);
    let id = "";
    if (u.hostname.includes("youtu.be")) {
      id = u.pathname.replace("/", "");
    } else if (u.searchParams.get("v")) {
      id = u.searchParams.get("v");
    } else if (u.pathname.includes("/embed/")) {
      id = u.pathname.split("/embed/")[1];
    } else if (u.pathname.includes("/shorts/")) {
      id = u.pathname.split("/shorts/")[1];
    }
    if (!id) return url;
    return `https://www.youtube.com/embed/${id}?autoplay=1&rel=0&modestbranding=1`;
  } catch {
    return url;
  }
};

const YouTubeModal = ({ url, onClose }) => {
  useEffect(() => {
    if (!url) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
    };
  }, [onClose, url]);

  return (
    <AnimatePresence>
      {url ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[100] bg-brand-navy/95 backdrop-blur-md flex items-center justify-center p-6"
          onClick={onClose}
          data-testid="youtube-modal"
        >
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-testid="youtube-close"
            className="absolute top-6 right-6 text-white w-12 h-12 border border-white/30 flex items-center justify-center hover:bg-brand-gold hover:border-brand-gold transition-colors"
          >
            <X size={20} strokeWidth={1.5} />
          </button>
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.19, 1, 0.22, 1] }}
            className="w-full max-w-6xl aspect-video bg-black shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <iframe
              title="Video"
              src={toYouTubeEmbed(url)}
              className="w-full h-full"
              allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
              allowFullScreen
            />
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};

export default YouTubeModal;
